import { Controller } from 'stimulus'
import { InputMasking } from 'classes/input-masking'

/**
 * @class GmAddressAutoCompleteController
 * @extends Controllers
 * @example
 * -# Example Markup (HAML)
 *
 * %span{ 'data-controller' => 'gm-address-autocomplete' }
 *   = text_field_tag :address, 'data-target' => 'gm-address-autocomplete.address'
 *   = text_field_tag :city, 'data-target' => 'gm-address-autocomplete.city'
 *   = select_tag :state, options_for_select(us_states, state), 'data-target' => 'gm-address-autocomplete.state'
 *   = text_field_tag :zip, 'data-target' => 'gm-address-autocomplete.zip'
 */
export default class extends Controller {
  static targets = [ 'search', 'address', 'city', 'state', 'zip' ]

  /**
   * @constructor
   */
  initialize() {
    this.GOOGLE_TIMEOUT = 5000
    this.GOOGLE_STREET_NUMBER = 'street_number'
    this.GOOGLE_STREET_ADDRESS = 'route'
    this.GOOGLE_STATE_TYPE = 'administrative_area_level_1'
    this.GOOGLE_CITY_TYPE = 'locality'
    this.GOOGLE_NEIGHBORHOOD_TYPE = 'sublocality_level_1'
    this.GOOGLE_ZIP_TYPE = 'postal_code'

    this._initOptions()
    this._initGoogleMapsClient()
  }

  /**
   * @function GmAddressAutoCompleteController.update
   */
  update() {
    const place = this.gmClient.getPlace()
    if (!place || !place.address_components) { return }

    let city = ''
    let longState = ''
    let streetAddress = ''
    let streetNumber = ''

    place.address_components.forEach(piece => {
      const type = piece.types[0]

      switch (type) {
        case this.GOOGLE_CITY_TYPE: {
          if (this.hasCityTarget) {
            this.cityTarget.value = piece.long_name
          }
          city = piece.long_name
          break
        }
        case this.GOOGLE_NEIGHBORHOOD_TYPE: {
          if (this.hasCityTarget) {
            this.cityTarget.value = piece.long_name
          }
          break
        }
        case this.GOOGLE_STATE_TYPE: {
          if (this.hasStateTarget) {
            this.stateTarget.value = piece.long_name
          }
          longState = piece.long_name
          break
        }
        case this.GOOGLE_ZIP_TYPE: {
          if (this.hasZipTarget) {
            this.zipTarget.value = piece.long_name
          }
          break
        }
        case this.GOOGLE_STREET_NUMBER: {
          streetNumber = piece.long_name
          break
        }
        case this.GOOGLE_STREET_ADDRESS: {
          streetAddress = piece.long_name
          break
        }
      }
    })

    if ( !this._doingMiniSearch ) {
      this.addressTarget.value = [ streetNumber, streetAddress ].join(' ')
    } else {
      this.addressTarget.value = `${city}, ${longState}, USA`
    }

    InputMasking.applyAll()

    // IE input hackery
    if (this.hasCityTarget) { this.cityTarget.focus() }
    if (this.hasZipTarget) { this.zipTarget.focus() }
  }

  //
  // "Private" methods
  //

  /**
   * @private
   */
  _initOptions() {
    this.options = {
      componentRestrictions: { country: 'US' }
    }

    if (this._doingMiniSearch) {
      this.options['types'] = [ '(cities)' ]
    } else {
      this.options['types'] = [ 'address' ]
    }
  }

  /**
   * @private
   */
  _initGoogleMapsClient() {
    const searchElem = this.hasSearchTarget ? this.searchTarget : this.addressTarget
    this._waitForGoogleMaps.then(() => {
      /* global google */
      this.gmClient = new google.maps.places.Autocomplete(searchElem, this.options)
      this.gmClient.addListener('place_changed', () => { this.update() })
    })
  }

  /**
   * @return {boolean}
   * @private
   */
  get _doingMiniSearch() {
    return !this.hasCityTarget || !this.hasStateTarget || !this.hasZipTarget
  }

  /**
   * @return {Promise}
   * @private
   */
  get _waitForGoogleMaps() {
    let timeout = this.GOOGLE_TIMEOUT
    const interval = 10

    /* global Promise */
    return new Promise((resolve, reject) => {
      const check = () => {
        if (typeof google !== 'undefined') {
          resolve()
        } else if ((timeout -= interval) < 0) {
          reject(new Error('timed out!'))
        } else {
          setTimeout(check, interval)
        }
      }
      setTimeout(check, interval)
    })
  }
}
